import React, { useEffect, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR from 'swr';
import { Bot, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCurrentZone } from '../hooks/useCurrentZone';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useT } from '@/contexts/I18nContext';
import {
  getTelegramSettings,
  initTelegramSettings,
  patchTelegramSettings,
  type TelegramSettingsDto,
} from '../api/telegram';
import {
  getCheckoutControlSettings,
  patchCheckoutControlSettings,
  getTableControlSettings,
  patchTableControlSettings,
  getZoneRemindersSettings,
  initZoneRemindersSettings,
  patchZoneRemindersSettings,
  getPeriodicReportsSettings,
  patchPeriodicReportsSettings,
} from '../api/integrations';
import { getVisitsZoneSettings, patchVisitsZoneChat } from '../api/visits';
import { getQueuesStaffSettings, patchQueuesStaffSettings, queuesStaffKey } from '../api/queuesStaff';
import { extractApiError as extractError } from '../utils/apiError';
import { getZone, updateZone } from '../api/zones';
import {
  getPinInvite,
  getPinPreview,
  sendPinToChat,
  type InviteDto,
} from '../api/invites';

// Bot handle to mention in the operator-facing setup instructions. Prod
// + staging both use linzarobot at the time of writing; if that ever
// diverges, prefer an env-injected value here.
const NOTIFICATIONS_BOT_HANDLE = '@linzarobot';

function is404(err: unknown): boolean {
  return typeof err === 'object' && err !== null && 'response' in err
    && (err as { response?: { status?: number } }).response?.status === 404;
}

// ─── Per-target row ──────────────────────────────────────────────────────────

interface TargetRowProps {
  label: string;
  description?: string;
  chatId: string | null | undefined;
  threadId: string | null | undefined;
  loading: boolean;
  loadError: string | null;
  onSave: (next: { chat_id: string | null; thread_id: string | null }) => Promise<void>;
}

function TargetRow({
  label,
  description,
  chatId,
  threadId,
  loading,
  loadError,
  onSave,
}: TargetRowProps) {
  const { t } = useT();
  const [draftChat, setDraftChat] = useState('');
  const [draftThread, setDraftThread] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setDraftChat(chatId ?? '');
    setDraftThread(threadId ?? '');
  }, [chatId, threadId]);

  const dirty =
    (draftChat ?? '').trim() !== (chatId ?? '').trim()
    || (draftThread ?? '').trim() !== (threadId ?? '').trim();

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await onSave({
        chat_id: draftChat.trim() || null,
        thread_id: draftThread.trim() || null,
      });
      setSuccess(true);
      window.setTimeout(() => setSuccess(false), 2000);
    } catch (e) {
      setError(extractError(e, t('telegram.saveFailed')));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid grid-cols-1 items-start gap-3 border-t border-border px-3 py-3 first:border-t-0 sm:grid-cols-[minmax(0,12rem)_minmax(0,1.6fr)_minmax(0,0.7fr)_auto]">
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {description && <div className="text-xs text-muted-foreground">{description}</div>}
      </div>

      <div className="min-w-0">
        <Input
          className="font-mono text-sm"
          value={draftChat}
          onChange={e => setDraftChat(e.target.value)}
          placeholder="-100123456789"
          disabled={loading}
        />
        {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
        {loadError && <ServerStatusNotice variant="card" className="mt-1" />}
      </div>

      <div className="min-w-0">
        <Input
          className="font-mono text-sm"
          value={draftThread}
          onChange={e => setDraftThread(e.target.value)}
          placeholder="—"
          disabled={loading}
        />
      </div>

      <Button
        size="sm"
        variant="outline"
        onClick={handleSave}
        disabled={!dirty || saving || loading}
      >
        {saving ? <Loader2 className="size-3 animate-spin" /> : success ? '✓' : t('common.save')}
      </Button>
    </div>
  );
}

// ─── Per-target hooks (each wraps the SWR + endpoint pair) ────────────────────

function useProdChatRow(zoneId: string, t: (k: string) => string) {
  const swrKey = `telegram:settings:${zoneId}`;
  const { data, error, isLoading, mutate } = useSWR<TelegramSettingsDto | null>(
    swrKey,
    async () => {
      try {
        const resp = await getTelegramSettings(zoneId);
        return resp.status === 204 ? null : resp.data;
      } catch (e) {
        if (is404(e)) return null;
        throw e;
      }
    },
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const onSave = async (next: { chat_id: string | null; thread_id: string | null }) => {
    // Initialise the row on first save if the BE returned 204/404.
    if (!data) {
      await initTelegramSettings(zoneId);
    }
    await patchTelegramSettings(zoneId, {
      prod_chat_id: next.chat_id ?? '',
      prod_thread_id: next.thread_id,
    });
    await mutate();
  };
  return {
    chatId: data?.prod_chat_id ?? null,
    threadId: data?.prod_thread_id ?? null,
    loading: isLoading,
    loadError: error && !is404(error) && data === undefined ? t('telegram.fetchFailed') : null,
    canConfigureSystemChats: data?.can_configure_system_chats ?? false,
    raw: data,
    mutate,
    onSave,
  };
}

function useTableControlRow(zoneId: string, t: (k: string) => string) {
  const swrKey = `table_control:settings:${zoneId}`;
  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      try {
        return (await getTableControlSettings(zoneId)).data;
      } catch (e) {
        if (is404(e)) return null;
        throw e;
      }
    },
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const onSave = async (next: { chat_id: string | null; thread_id: string | null }) => {
    await patchTableControlSettings(zoneId, {
      table_control_chat_id: next.chat_id,
      table_control_thread_id: next.thread_id,
    });
    await mutate();
  };
  return {
    chatId: data?.table_control_chat_id ?? null,
    threadId: data?.table_control_thread_id ?? null,
    loading: isLoading,
    loadError: error && !is404(error) && data === undefined ? t('telegram.fetchFailed') : null,
    onSave,
  };
}

function useCheckoutControlRow(zoneId: string, t: (k: string) => string) {
  const swrKey = `checkout_control:settings:${zoneId}`;
  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      try {
        return (await getCheckoutControlSettings(zoneId)).data;
      } catch (e) {
        if (is404(e)) return null;
        throw e;
      }
    },
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const onSave = async (next: { chat_id: string | null; thread_id: string | null }) => {
    await patchCheckoutControlSettings(zoneId, {
      checkout_control_chat_id: next.chat_id,
      checkout_control_thread_id: next.thread_id,
    });
    await mutate();
  };
  return {
    chatId: data?.checkout_control_chat_id ?? null,
    threadId: data?.checkout_control_thread_id ?? null,
    loading: isLoading,
    loadError: error && !is404(error) && data === undefined ? t('telegram.fetchFailed') : null,
    onSave,
  };
}

function useChecklistsRow(zoneId: string, t: (k: string) => string) {
  const swrKey = `ops_tasks:reminders:${zoneId}`;
  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      try {
        const r = await getZoneRemindersSettings(zoneId);
        return r.status === 204 ? null : r.data;
      } catch (e) {
        if (is404(e)) return null;
        throw e;
      }
    },
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const onSave = async (next: { chat_id: string | null; thread_id: string | null }) => {
    if (!data) {
      await initZoneRemindersSettings(zoneId);
    }
    await patchZoneRemindersSettings(zoneId, {
      ops_tasks_chat_id: next.chat_id,
      ops_tasks_thread_id: next.thread_id,
    });
    await mutate();
  };
  return {
    chatId: data?.ops_tasks_chat_id ?? null,
    threadId: data?.ops_tasks_thread_id ?? null,
    loading: isLoading,
    loadError: error && !is404(error) && data === undefined ? t('telegram.fetchFailed') : null,
    onSave,
  };
}

function useDailyReportsRow(zoneId: string, t: (k: string) => string) {
  const swrKey = `periodic_reports:settings:${zoneId}`;
  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      try {
        return (await getPeriodicReportsSettings(zoneId)).data;
      } catch (e) {
        if (is404(e)) return null;
        throw e;
      }
    },
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const onSave = async (next: { chat_id: string | null; thread_id: string | null }) => {
    await patchPeriodicReportsSettings(zoneId, {
      daily_report_chat_id: next.chat_id,
      daily_report_thread_id: next.thread_id,
    });
    await mutate();
  };
  return {
    chatId: data?.daily_report_chat_id ?? null,
    threadId: data?.daily_report_thread_id ?? null,
    loading: isLoading,
    loadError: error && !is404(error) && data === undefined ? t('telegram.fetchFailed') : null,
    onSave,
  };
}

function useVisitsRow(zoneId: string, t: (k: string) => string) {
  const swrKey = `visits:settings:${zoneId}`;
  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      try {
        return (await getVisitsZoneSettings(zoneId)).data;
      } catch (e) {
        if (is404(e)) return null;
        throw e;
      }
    },
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const onSave = async (next: { chat_id: string | null; thread_id: string | null }) => {
    await patchVisitsZoneChat(zoneId, next.chat_id, next.thread_id);
    await mutate();
  };
  return {
    chatId: data?.visits_chat_id ?? null,
    threadId: data?.visits_thread_id ?? null,
    loading: isLoading,
    loadError: error && !is404(error) && data === undefined ? t('telegram.fetchFailed') : null,
    onSave,
  };
}

// Приход к открытию (#1798): строка есть, только пока функция и утренние сообщения
// включены; пустой чат — сообщения идут в основной чат филиала. Кэш общий с
// «Очереди и сотрудники», правка там видна тут сразу.
function useStaffArrivalRow(zoneId: string, t: (k: string) => string) {
  const { data, error, isLoading, mutate } = useSWR(
    zoneId ? queuesStaffKey(zoneId) : null,
    async () => (await getQueuesStaffSettings(zoneId)).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const onSave = async (next: { chat_id: string | null; thread_id: string | null }) => {
    const r = await patchQueuesStaffSettings(zoneId, { arrival_chat_id: next.chat_id, arrival_thread_id: next.thread_id });
    await mutate(r.data, { revalidate: false });
  };
  return {
    visible: !!(data?.arrival_enabled && data.arrival_messages),
    chatId: data?.arrival_chat_id ?? null,
    threadId: data?.arrival_thread_id ?? null,
    loading: isLoading,
    loadError: error && !is404(error) && data === undefined ? t('telegram.fetchFailed') : null,
    onSave,
  };
}

// ─── System (debug + cancel) chats — superuser-only, collapsed ───────────────

function SystemChatsSection({
  zoneId,
  initialDebug,
  initialCancel,
  refresh,
}: {
  zoneId: string;
  initialDebug: string;
  initialCancel: string;
  refresh: () => void;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [debugChatId, setDebugChatId] = useState(initialDebug);
  const [cancelChatId, setCancelChatId] = useState(initialCancel);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setDebugChatId(initialDebug);
    setCancelChatId(initialCancel);
  }, [initialDebug, initialCancel]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await patchTelegramSettings(zoneId, {
        debug_chat_id: debugChatId.trim(),
        cancel_chat_id: cancelChatId.trim(),
      });
      refresh();
      setSuccess(true);
      window.setTimeout(() => setSuccess(false), 2000);
    } catch (e) {
      setError(extractError(e, t('telegram.saveFailed')));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-xs text-muted-foreground transition-colors hover:bg-muted/40"
      >
        <span>{t('telegram.systemSection')}</span>
        {open ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
      </button>
      {open && (
        <div className="space-y-3 border-t border-border p-3 text-sm">
          <p className="text-xs text-warning">{t('telegram.systemWarning')}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1">
              <Label className="text-xs">{t('telegram.debugChatLabel')}</Label>
              <Input
                className="font-mono text-sm"
                value={debugChatId}
                onChange={e => setDebugChatId(e.target.value)}
              />
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">{t('telegram.cancelChatLabel')}</Label>
              <Input
                className="font-mono text-sm"
                value={cancelChatId}
                onChange={e => setCancelChatId(e.target.value)}
              />
            </div>
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          {success && <p className="text-xs text-success">{t('telegram.saved')}</p>}
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="size-3 animate-spin mr-1" />}
            {t('common.save')}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Pin-message section (per-zone template URLs + send/repin) ───────────────

interface PinSectionProps {
  zoneId: string;
  defaultChatId: string | null;
}

function PinMessageSection({ zoneId, defaultChatId }: PinSectionProps) {
  const { t } = useT();
  const zoneSwr = useSWR(['zone', zoneId], () => getZone(zoneId).then(r => r.data));
  const inviteSwr = useSWR<InviteDto>(['pin-invite', zoneId], () =>
    getPinInvite(zoneId).then(r => r.data),
  );
  const previewSwr = useSWR(['pin-preview', zoneId], () =>
    getPinPreview(zoneId).then(r => r.data.text),
  );

  // Local editor state for the 4 URL fields; mirrors the loaded zone
  // values, dirty flag derived from comparison.
  const [chatInvite, setChatInvite] = useState('');
  const [savingFields, setSavingFields] = useState(false);
  const [fieldsError, setFieldsError] = useState<string | null>(null);
  const [fieldsSaved, setFieldsSaved] = useState(false);

  useEffect(() => {
    const z = zoneSwr.data;
    if (!z) return;
    setChatInvite(z.chat_invite_url ?? '');
  }, [zoneSwr.data]);

  const dirty = (zoneSwr.data?.chat_invite_url ?? '') !== chatInvite;

  async function handleSaveFields() {
    setSavingFields(true);
    setFieldsError(null);
    setFieldsSaved(false);
    try {
      // Grafana URLs are no longer edited here (2026-08-10) — Grafana is being
      // retired, so the pin carries only the cabinet + the chat invite. The zone
      // columns still exist; we simply stop writing them from this screen.
      await updateZone(zoneId, { chat_invite_url: chatInvite.trim() });
      await Promise.all([zoneSwr.mutate(), previewSwr.mutate()]);
      setFieldsSaved(true);
      window.setTimeout(() => setFieldsSaved(false), 2000);
    } catch (e) {
      setFieldsError(extractError(e, t('telegram.saveFailed')));
    } finally {
      setSavingFields(false);
    }
  }

  // Send-to-chat state — separate so saving fields doesn't block
  // sending and vice versa.
  const [chatIdOverride, setChatIdOverride] = useState('');
  const [threadIdOverride, setThreadIdOverride] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  async function handleSend() {
    setSending(true);
    setSendError(null);
    try {
      await sendPinToChat(
        zoneId,
        chatIdOverride.trim() || undefined,
        threadIdOverride.trim() || undefined,
      );
      await inviteSwr.mutate();
    } catch (e) {
      setSendError(extractError(e, t('telegram.pin.sendFailed')));
    } finally {
      setSending(false);
    }
  }

  const pinned = inviteSwr.data?.pinned_in_chat_at ?? null;

  return (
    <Card variant="section">
      <CardContent className="space-y-4 pt-5">
        <div>
          <h2 className="text-sm font-semibold text-foreground">
            {t('telegram.pin.title')}
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('telegram.pin.subtitle')}
          </p>
        </div>

        {/* Per-zone URL editor — empty fields are dropped from the
            rendered pin, so operators see only the sections they've
            configured. */}
        <div className="grid gap-3">
          <div className="grid gap-1">
            <Label className="text-xs">{t('telegram.pin.field.chatInvite')}</Label>
            <Input
              className="font-mono text-xs"
              placeholder="https://t.me/+..."
              value={chatInvite}
              onChange={e => setChatInvite(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSaveFields} disabled={savingFields || !dirty}>
              {savingFields && <Loader2 className="mr-1 size-3 animate-spin" />}
              {t('common.save')}
            </Button>
            {fieldsSaved && <span className="text-xs text-success">{t('telegram.saved')}</span>}
            {fieldsError && <span className="text-xs text-destructive">{fieldsError}</span>}
          </div>
        </div>

        {/* Preview pane — read-only, shows the BE-rendered HTML in
            plain text so the operator can see exactly what the bot
            will send. */}
        <div className="grid gap-1">
          <Label className="text-xs">{t('telegram.pin.previewLabel')}</Label>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted/30 p-3 font-mono text-[11px] leading-relaxed">
            {previewSwr.data ?? t('common.loading')}
          </pre>
        </div>

        {/* Send-to-chat row — defaults to the zone's configured prod_chat_id
            (shown as placeholder), can be overridden for one-offs. Pin
            status badge shows when the bot last successfully pinned. */}
        <div className="grid gap-1">
          <Label className="text-xs">{t('telegram.pin.chatIdLabel')}</Label>
          <Input
            className="font-mono text-sm"
            placeholder={defaultChatId ?? t('telegram.pin.chatIdPlaceholder')}
            value={chatIdOverride}
            onChange={e => setChatIdOverride(e.target.value)}
          />
          <p className="text-[11px] text-muted-foreground">{t('telegram.pin.chatIdHint')}</p>
        </div>

        {/* Forum topic. Telegram's Bot API has no way to LIST a chat's topics
            (there is no getForumTopics), so this cannot be a dropdown — the
            operator reads the id off a message link in Telegram Web. */}
        <div className="grid gap-1">
          <Label className="text-xs">{t('telegram.pin.threadIdLabel')}</Label>
          <Input
            className="font-mono text-sm"
            placeholder={t('telegram.pin.threadIdPlaceholder')}
            value={threadIdOverride}
            onChange={e => setThreadIdOverride(e.target.value)}
          />
          <p className="text-[11px] text-muted-foreground">{t('telegram.pin.threadIdHint')}</p>
        </div>

        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-800 dark:text-amber-200">
          {t('telegram.pin.adminNote')}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={handleSend} disabled={sending}>
            {sending && <Loader2 className="mr-1 size-3 animate-spin" />}
            {pinned ? t('telegram.pin.update') : t('telegram.pin.send')}
          </Button>
          {pinned ? (
            <span className="text-xs text-muted-foreground">
              {t('telegram.pin.lastPinnedAt', { at: new Date(pinned).toLocaleString() })}
            </span>
          ) : (
            <span className="text-xs text-amber-700 dark:text-amber-300">
              {t('telegram.pin.notPinned')}
            </span>
          )}
          {sendError && <span className="text-xs text-destructive">{sendError}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Root ────────────────────────────────────────────────────────────────────

const TelegramBotSettings: React.FC = () => {
  const { t } = useT();
  const { zoneId } = useCurrentZone();
  const { user } = useCurrentUser();
  const isSuperuser = user?.is_superuser === true;

  const prod = useProdChatRow(zoneId ?? '', t);
  const tables = useTableControlRow(zoneId ?? '', t);
  const queues = useCheckoutControlRow(zoneId ?? '', t);
  const checklists = useChecklistsRow(zoneId ?? '', t);
  const daily = useDailyReportsRow(zoneId ?? '', t);
  const visits = useVisitsRow(zoneId ?? '', t);
  const arrival = useStaffArrivalRow(zoneId ?? '', t);

  if (!zoneId) {
    return <BranchRequiredState />;
  }

  return (
    <div className="space-y-4">
      {/* Per-target rows — one per notification kind. Each row is
          independent (own loader, own save button) so a failure on
          one row doesn't block the others. The setup hint lives at
          the bottom of the page (small + secondary — it's help
          text, not the primary thing to do here). */}
      <Card variant="section">
        <CardContent className="p-0">
          {/* Column header is meaningful only in the wide (sm+) table layout;
              when rows stack to single-column on <sm it would be a confusing
              orphan, so it is hidden there. */}
          <div className="hidden grid-cols-[minmax(0,12rem)_minmax(0,1.6fr)_minmax(0,0.7fr)_auto] items-center gap-3 border-b border-border bg-muted/40 px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground sm:grid">
            <div>{t('telegram.targets.header.target')}</div>
            <div>{t('telegram.targets.header.chatId')}</div>
            <div>{t('telegram.targets.header.threadId')}</div>
            <div />
          </div>

          <TargetRow
            label={t('telegram.targets.layout.label')}
            description={t('telegram.targets.layout.description')}
            chatId={prod.chatId}
            threadId={prod.threadId}
            loading={prod.loading}
            loadError={prod.loadError}
            onSave={prod.onSave}
          />
          <TargetRow
            label={t('telegram.targets.tables.label')}
            description={t('telegram.targets.tables.description')}
            chatId={tables.chatId}
            threadId={tables.threadId}
            loading={tables.loading}
            loadError={tables.loadError}
            onSave={tables.onSave}
          />
          <TargetRow
            label={t('telegram.targets.queues.label')}
            description={t('telegram.targets.queues.description')}
            chatId={queues.chatId}
            threadId={queues.threadId}
            loading={queues.loading}
            loadError={queues.loadError}
            onSave={queues.onSave}
          />
          {arrival.visible && (
            <TargetRow
              label={t('telegram.targets.staffArrival.label')}
              description={t('telegram.targets.staffArrival.description')}
              chatId={arrival.chatId}
              threadId={arrival.threadId}
              loading={arrival.loading}
              loadError={arrival.loadError}
              onSave={arrival.onSave}
            />
          )}
          <TargetRow
            label={t('telegram.targets.checklists.label')}
            description={t('telegram.targets.checklists.description')}
            chatId={checklists.chatId}
            threadId={checklists.threadId}
            loading={checklists.loading}
            loadError={checklists.loadError}
            onSave={checklists.onSave}
          />
          <TargetRow
            label={t('telegram.targets.daily.label')}
            description={t('telegram.targets.daily.description')}
            chatId={daily.chatId}
            threadId={daily.threadId}
            loading={daily.loading}
            loadError={daily.loadError}
            onSave={daily.onSave}
          />
          <TargetRow
            label={t('telegram.targets.visits.label')}
            description={t('telegram.targets.visits.description')}
            chatId={visits.chatId}
            threadId={visits.threadId}
            loading={visits.loading}
            loadError={visits.loadError}
            onSave={visits.onSave}
          />
        </CardContent>
      </Card>

      {/* Pin-message section — per-zone template URLs + explicit
          "Send to chat" action. Replaces the pre-2026-05-26 auto-pin
          behaviour where invite create/reissue silently spammed chats. */}
      <PinMessageSection zoneId={zoneId} defaultChatId={prod.chatId} />

      {/* System chats (debug/cancel) — superuser-only, collapsed
          by default. Kept on this page because they share the
          TelegramSettings model with the main "Выкладка" row. */}
      {isSuperuser && prod.canConfigureSystemChats && prod.raw && (
        <SystemChatsSection
          zoneId={zoneId}
          initialDebug={prod.raw.debug_chat_id ?? ''}
          initialCancel={prod.raw.cancel_chat_id ?? ''}
          refresh={() => void prod.mutate()}
        />
      )}

      {/* Setup hint at the BOTTOM — it's help text, not the primary
          action on this page. Compact + muted so it reads as
          reference material rather than competing with the targets
          table for attention. */}
      <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        <Bot className="mt-0.5 size-3.5 shrink-0" />
        <div className="space-y-1">
          <p>
            <span className="font-medium text-foreground">{t('telegram.setupHint.title')}.</span>{' '}
            {t('telegram.setupHint.body').replace('{{bot}}', NOTIFICATIONS_BOT_HANDLE)}
          </p>
          <p>{t('telegram.setupHint.threads')}</p>
        </div>
      </div>
    </div>
  );
};

export default TelegramBotSettings;
